// RTL.SAFETY.1 — géométrie drone -> HOME (lecture seule, jamais fabriquée). Aucune commande.
const finite = (v) => typeof v === "number" && Number.isFinite(v);
const R = 6371000;
const rad = (d) => (d * Math.PI) / 180;

export const RTL_DISCLAIMER = "Le RTL PX4 ne garantit ni trajectoire directe ni évitement d'obstacles (montée/descente et marges selon paramètres PX4).";

export function homeGeometry({ droneLat, droneLon, heading, homeLat, homeLon }) {
  if (![droneLat, droneLon, homeLat, homeLon].every(finite)) return null;
  const p1 = rad(droneLat), p2 = rad(homeLat), dl = rad(homeLon - droneLon);
  const a = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const distance = 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  let relative = null, side = null;
  if (finite(heading)) {
    relative = ((bearing - heading + 540) % 360) - 180;
    const r = Math.abs(relative);
    side = r <= 45 ? "devant" : r >= 135 ? "derrière" : relative > 0 ? "à droite" : "à gauche";
  }
  return { distance, bearing, relative, side };
}

// Avertissement opérateur avant RTL volontaire. `home` = homePositionView(status) ou équivalent.
export function rtlAdvisory({ home, drone }) {
  if (!home || home.valid !== true) {
    return { level: "danger", geometry: null, text: "HOME non valide — RTL SkyCore refusé par l'Agent. Utiliser LAND ou la RC." };
  }
  const g = homeGeometry({ droneLat: drone?.lat, droneLon: drone?.lon ?? drone?.lng, heading: drone?.heading,
    homeLat: home.lat, homeLon: home.lon });
  if (!g) return { level: "warn", geometry: null, text: `Position drone indisponible — direction du HOME inconnue. ${RTL_DISCLAIMER}` };
  const where = g.side ? ` ${g.side}` : "";
  return {
    level: g.distance < 10 ? "warn" : "info",
    geometry: g,
    text: `Le drone va rejoindre le HOME situé${where} à ${g.distance.toFixed(1)} m (cap ${Math.round(g.bearing)}°). ${RTL_DISCLAIMER}`,
  };
}

export function rtlConfirmMessage(drone) {
  const home = { valid: drone?.home_position_valid === true, lat: drone?.home_lat, lon: drone?.home_lon };
  if (home.valid && !(finite(home.lat) && finite(home.lon))) home.valid = false;
  return `Confirmer le retour automatique (Return To Launch) ? ${rtlAdvisory({ home, drone }).text}`;
}

// RTL.SAFETY.1-R2 — préconditions UI communes (Dashboard / CommandCenter / cockpit). L'Agent reste
// l'autorité finale (gate HOME fail-closed) ; l'UI n'affiche jamais un RTL envoyable sans ces preuves.
export function rtlGate(drone, nowMs = Date.now(), staleS = 6) {
  if (!drone) return { allowed: false, reason: "Drone inconnu" };
  if (drone.status !== "online") return { allowed: false, reason: "Drone hors ligne" };
  const seen = drone.last_seen ? Date.parse(drone.last_seen) : NaN;
  if (drone.telemetry_stale === true || !Number.isFinite(seen) || (nowMs - seen) / 1000 > staleS)
    return { allowed: false, reason: "Télémétrie périmée" };
  if (drone.drone_ws_connected !== true || drone.mavlink_connected !== true)
    return { allowed: false, reason: "Liaison drone/PX4 indisponible" };
  if (drone.armed !== true) return { allowed: false, reason: "Drone non armé" };
  if (drone.landed_state === 1) return { allowed: false, reason: "Drone au sol" };
  if (!drone.gps_fix) return { allowed: false, reason: "GPS 3D requis" };
  if (drone.home_position_valid !== true || !finite(drone.home_lat) || !finite(drone.home_lon))
    return { allowed: false, reason: "HOME non valide" };
  return { allowed: true, reason: null };
}
